import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth, API } from "../App";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";

export const money = (cents) => ((cents || 0) / 100).toFixed(2);

export const usePurchaseQuote = (items, enabled = true) => {
  const { token } = useAuth();
  const key = JSON.stringify(items);
  const [state, setState] = useState({ quote: null, loading: false, error: "" });
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!enabled || !token || !JSON.parse(key).length) {
      setState({ quote: null, loading: false, error: token ? "" : "Sign in to calculate your total", status: token ? 0 : 401 });
      return;
    }
    const controller = new AbortController();
    setState({ quote: null, loading: true, error: "" });
    axios.post(`${API}/wallet/quote`, { items: JSON.parse(key) }, { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal })
      .then(({ data }) => { if (!controller.signal.aborted) setState({ quote: data, loading: false, error: "" }); })
      .catch((error) => {
        if (controller.signal.aborted) return;
        const detail = error.response?.data?.detail;
        setState({ quote: null, loading: false, status: error.response?.status,
          error: typeof detail === "string" ? detail : detail?.message || "Your total could not be calculated" });
      });
    return () => controller.abort();
  }, [key, token, enabled, revision]);
  return { ...state, refresh: () => setRevision((value) => value + 1) };
};

export const QuoteSummary = ({ state, prefix, onNavigate }) => {
  const { quote, loading, error, status } = state;
  if (loading) return <p data-testid={`${prefix}-quote-loading`} className="text-sm text-[#8A92A0] flex gap-2"><Loader2 className="w-4 h-4 animate-spin" />Calculating total…</p>;
  if (error) return <div className="text-sm space-y-2" data-testid={`${prefix}-quote-error`} role="alert">
    <p className="text-[#F0C464] break-words">{error}</p>
    {[401, 423, 428].includes(status) ? <Link onClick={onNavigate} data-testid={`${prefix}-resolve-address`} className="text-[#FF66BB] underline" to={status === 401 ? "/auth" : status === 423 ? "/id-vault" : "/wallet"}>{status === 401 ? "Sign in" : status === 423 ? "Review verification" : "Confirm billing address"}</Link>
      : <button data-testid={`${prefix}-quote-retry`} className="underline" onClick={state.refresh}>Retry</button>}
  </div>;
  if (!quote) return null;
  return <dl data-testid={`${prefix}-quote`} className="text-sm space-y-2 text-[#8A92A0]">
    <div className="flex justify-between gap-3"><dt>Subtotal</dt><dd data-testid={`${prefix}-subtotal`}>${money(quote.subtotal_cents)}</dd></div>
    <div className="flex justify-between gap-3"><dt>Sales tax</dt><dd data-testid={`${prefix}-tax`}>${money(quote.tax_cents)}</dd></div>
    <div className="flex justify-between gap-3 border-t border-[#1A2030] pt-2 text-white font-semibold"><dt>Total</dt><dd data-testid={`${prefix}-total`}>{money(quote.total_cents)} Tokens</dd></div>
    <div className="flex justify-between gap-3"><dt>Spendable Tokens</dt><dd data-testid={`${prefix}-balance`}>{money(quote.balance_cents)}</dd></div>
    <div className="flex justify-between gap-3"><dt>Stripe top-up</dt><dd data-testid={`${prefix}-topup`}>${money(quote.topup_cents)}</dd></div>
    {quote.leftover_credit_cents > 0 && <div className="flex justify-between gap-3"><dt>Tokens remaining after purchase</dt><dd data-testid={`${prefix}-remaining`}>{money(quote.leftover_credit_cents)}</dd></div>}
    <div className="text-xs break-words" data-testid={`${prefix}-destination`}>{quote.location}</div>
  </dl>;
};

export const PurchaseReview = ({ purchase, onClose, onComplete }) => {
  const { token } = useAuth();
  const state = usePurchaseQuote(purchase ? [purchase] : [], !!purchase);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { setError(""); }, [purchase]);
  const pay = async () => {
    if (!state.quote || busy) return;
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/wallet/pass-through-checkout`, {
        ...purchase, expected_total_cents: state.quote.total_cents,
        success_url: `${window.location.origin}/checkout/complete?item_type=${purchase.item_type}`,
        cancel_url: window.location.href,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (data.path === "stripe_topup") window.location.assign(data.checkout_url);
      else if (onComplete) { onComplete(data); onClose(); }
      else if (purchase.item_type === "collectible") window.location.assign("/my-collectibles");
      else window.location.assign("/wallet");
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : detail?.message || "Payment could not be completed");
      state.refresh();
    } finally { setBusy(false); }
  };
  return <Dialog open={!!purchase} onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
    <DialogContent data-testid="purchase-review-dialog" className="bg-[#0A0E18] text-white border-[#1A2030] w-[calc(100%-2rem)] max-w-md max-h-[85dvh] overflow-y-auto">
      <DialogTitle data-testid="purchase-review-title">Review purchase</DialogTitle>
      <DialogDescription data-testid="purchase-review-item" className="text-[#8A92A0] break-words">{state.quote?.lines?.[0]?.name || "Order total"}</DialogDescription>
      <QuoteSummary state={state} prefix="purchase-review" onNavigate={onClose} />
      {purchase?.item_type === "collectible" && <p data-testid="collectible-purchase-policy" className="text-sm text-[#F0C464]">This purchase adds one edition with one PNG download. You may purchase more editions while supply remains.</p>}
      {error && <p data-testid="purchase-review-error" role="alert" className="text-sm text-[#FF66BB] break-words">{error}</p>}
      <Button data-testid="purchase-review-pay" className="bg-[#EC008C] hover:bg-[#FF3399] text-black" disabled={!state.quote || state.loading || busy} onClick={pay}>
        {busy ? "Processing…" : state.quote?.topup_cents ? `Add $${money(state.quote.topup_cents)} and buy` : `Pay ${money(state.quote?.total_cents)} Tokens`}
      </Button>
      <Button data-testid="purchase-review-cancel" variant="outline" disabled={busy} onClick={onClose}>Cancel</Button>
    </DialogContent>
  </Dialog>;
};